import { Box, Typography } from "@mui/material";
import { formatMoney } from "../formatters";
import { itemResolutionText, requestTerm } from "../status/returnState";
import {
  statusColors,
  statusPanelSx,
  statusSectionTitleSx,
} from "../statusStyles";
import type { Claim, ClaimItem, ClaimTranslator } from "../types";

type ClaimItemsListProps = {
  items?: ClaimItem[];
  claim: Claim;
  t: ClaimTranslator;
};

export const ClaimItemsList = ({ items, claim, t }: ClaimItemsListProps) => {
  if (!items?.length) {
    return null;
  }

  return (
    <Box mb="16px" sx={{ ...statusPanelSx(), overflow: "hidden", padding: 0 }}>
      <Typography sx={{ ...statusSectionTitleSx, padding: "16px 16px 0" }}>
        {t("status.claimItems", { requestType: requestTerm(claim) })}
      </Typography>
      <Box>
        {items.map((item) => (
          <Box
            key={item.line_item_id}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
            sx={{
              padding: "14px 16px",
              borderBottom: `1px solid ${statusColors.divider}`,
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Box display="flex" alignItems="center" gap="12px" minWidth={0}>
              {item.image_url ? (
                <Box
                  component="img"
                  src={item.image_url}
                  alt=""
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <Box minWidth={0}>
                <Typography fontWeight={600} fontSize="15px" noWrap>
                  {item.product_name || item.line_item_id}
                </Typography>
                <Typography fontSize="14px" color={statusColors.metaText}>
                  Qty {item.quantity}
                  {item.price != null
                    ? ` · ${formatMoney(item.price, claim.order?.currency)}`
                    : ""}
                </Typography>
                <ItemResolution item={item} claim={claim} t={t} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ItemResolution = ({
  item,
  claim,
  t,
}: {
  item: ClaimItem;
  claim: Claim;
  t: ClaimTranslator;
}) => {
  const text = itemResolutionText(item, claim.order?.currency, t);
  if (!text) return null;
  return (
    <Typography fontSize="14px" fontWeight={600} color={statusColors.metaText}>
      {text}
    </Typography>
  );
};
